/**
 * MembershipsManagementTab.js
 * Admin component for configuring Professional and Premium membership plan pricing & AutoPay Plan IDs,
 * with separate independent saving for promotional Free Plan Mock Expiry date/time.
 * Restricted to ADMIN and SUPER_ADMIN roles.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { vendorService } from '../../../services/api';
import useAuth from '../../../hooks/useAuth';
import {
  Settings, RefreshCw, Check, Loader, AlertCircle, ShieldAlert, CreditCard, Clock, Sparkles, Save
} from 'lucide-react';

const formatForDatetimeLocal = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const MembershipsManagementTab = ({ toast }) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole && hasRole('ADMIN');

  const [loading, setLoading] = useState(true);
  
  // Separate saving states for Plans Pricing vs Free Expiry
  const [savingPlans, setSavingPlans] = useState(false);
  const [savedPlansOk, setSavedPlansOk] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [savingExpiry, setSavingExpiry] = useState(false);
  const [savedExpiryOk, setSavedExpiryOk] = useState(false);

  const [nowTs, setNowTs] = useState(Date.now());

  const [form, setForm] = useState({
    professional_price: 12000,
    premium_price: 25000,
    professional_plan_id: 'plan_TabHs7Uuj3A13W',
    premium_plan_id: 'plan_TabLDPpcFqUt7Z',
    individual_professional_price: 4000,
    individual_premium_price: 7000,
    individual_professional_plan_id: 'plan_TiFXhEqd1Msk0m',
    individual_premium_plan_id: 'plan_TiFaJ2pWDUDWay',
    free_plan_mock_expiry: '',
  });

  // Keep live preview ticking
  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getAdminMembershipConfig();
      if (res.success && res.data) {
        setForm({
          professional_price: res.data.professional_price ?? 12000,
          premium_price: res.data.premium_price ?? 25000,
          professional_plan_id: res.data.professional_plan_id || 'plan_TabHs7Uuj3A13W',
          premium_plan_id: res.data.premium_plan_id || 'plan_TabLDPpcFqUt7Z',
          individual_professional_price: res.data.individual_professional_price ?? 4000,
          individual_premium_price: res.data.individual_premium_price ?? 7000,
          individual_professional_plan_id: res.data.individual_professional_plan_id || 'plan_TiFXhEqd1Msk0m',
          individual_premium_plan_id: res.data.individual_premium_plan_id || 'plan_TiFaJ2pWDUDWay',
          free_plan_mock_expiry: formatForDatetimeLocal(res.data.free_plan_mock_expiry),
        });
      } else {
        toast.error(res.error || 'Failed to load membership pricing configuration.');
      }
    } catch (err) {
      console.error('Error fetching membership config:', err);
      toast.error(err?.message || err?.detail || 'Failed to load membership configuration.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchConfig();
    }
  }, [isAdmin, fetchConfig]);

  const applyPreset = (type) => {
    const target = new Date();
    if (type === '24h') {
      target.setHours(target.getHours() + 24);
    } else if (type === '3d') {
      target.setDate(target.getDate() + 3);
      target.setHours(23, 59, 0, 0);
    } else if (type === '7d') {
      target.setDate(target.getDate() + 7);
      target.setHours(23, 59, 0, 0);
    } else if (type === 'eom') {
      const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0);
      lastDay.setHours(23, 59, 0, 0);
      setForm((f) => ({ ...f, free_plan_mock_expiry: formatForDatetimeLocal(lastDay) }));
      return;
    } else if (type === 'clear') {
      setForm((f) => ({ ...f, free_plan_mock_expiry: '' }));
      return;
    }
    setForm((f) => ({ ...f, free_plan_mock_expiry: formatForDatetimeLocal(target) }));
  };

  const getPreviewInfo = (expiryStr) => {
    if (!expiryStr) return null;
    const target = new Date(expiryStr).getTime();
    if (isNaN(target)) return null;
    const diff = target - nowTs;
    if (diff <= 0) return { isExpired: true };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    const countdown = days > 0
      ? `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`
      : `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;

    const dateFormatted = new Date(expiryStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    return { isExpired: false, countdown, dateFormatted };
  };

  const previewInfo = getPreviewInfo(form.free_plan_mock_expiry);

  // ── 1. Save Company & Individual Plans Pricing ────────────────────────────
  const handleSavePlans = async () => {
    if (
      form.professional_price <= 0 ||
      form.premium_price <= 0 ||
      form.individual_professional_price <= 0 ||
      form.individual_premium_price <= 0
    ) {
      toast.error('All plan prices must be positive numbers greater than 0.');
      return;
    }
    if (
      !form.professional_plan_id?.trim() ||
      !form.premium_plan_id?.trim() ||
      !form.individual_professional_plan_id?.trim() ||
      !form.individual_premium_plan_id?.trim()
    ) {
      toast.error('Razorpay AutoPay Plan IDs cannot be empty.');
      return;
    }

    setSavingPlans(true);
    setSavedPlansOk(false);
    try {
      const res = await vendorService.updateAdminMembershipConfig({
        professional_price: Number(form.professional_price),
        premium_price: Number(form.premium_price),
        professional_plan_id: form.professional_plan_id.trim(),
        premium_plan_id: form.premium_plan_id.trim(),
        individual_professional_price: Number(form.individual_professional_price),
        individual_premium_price: Number(form.individual_premium_price),
        individual_professional_plan_id: form.individual_professional_plan_id.trim(),
        individual_premium_plan_id: form.individual_premium_plan_id.trim(),
      });

      if (res.success) {
        toast.success(res.message || 'Membership plan configurations updated successfully!');
        setSavedPlansOk(true);
        if (res.data?.config) {
          setForm((f) => ({
            ...f,
            professional_price: res.data.config.professional_price,
            premium_price: res.data.config.premium_price,
            professional_plan_id: res.data.config.professional_plan_id,
            premium_plan_id: res.data.config.premium_plan_id,
            individual_professional_price: res.data.config.individual_professional_price ?? 4000,
            individual_premium_price: res.data.config.individual_premium_price ?? 7000,
            individual_professional_plan_id: res.data.config.individual_professional_plan_id ?? 'plan_TiFXhEqd1Msk0m',
            individual_premium_plan_id: res.data.config.individual_premium_plan_id ?? 'plan_TiFaJ2pWDUDWay',
          }));
        }
        setTimeout(() => setSavedPlansOk(false), 3000);
      } else {
        toast.error(res.error || res.message || 'Failed to update membership configurations.');
      }
    } catch (err) {
      console.error('Error updating membership config:', err);
      toast.error(err?.message || err?.detail || 'Failed to update membership configurations.');
    } finally {
      setSavingPlans(false);
    }
  };

  // ── 2. Save Free Plan Mock Expiry Separately ─────────────────────────────
  const handleSaveExpiry = async () => {
    setSavingExpiry(true);
    setSavedExpiryOk(false);
    try {
      const res = await vendorService.updateAdminMembershipConfig({
        free_plan_mock_expiry: form.free_plan_mock_expiry ? new Date(form.free_plan_mock_expiry).toISOString() : '',
      });

      if (res.success) {
        toast.success('Free Plan mock promotional expiry updated successfully!');
        setSavedExpiryOk(true);
        if (res.data?.config) {
          setForm((f) => ({
            ...f,
            free_plan_mock_expiry: formatForDatetimeLocal(res.data.config.free_plan_mock_expiry),
          }));
        }
        setTimeout(() => setSavedExpiryOk(false), 3000);
      } else {
        toast.error(res.error || res.message || 'Failed to update mock expiry.');
      }
    } catch (err) {
      console.error('Error updating mock expiry:', err);
      toast.error(err?.message || err?.detail || 'Failed to update mock expiry.');
    } finally {
      setSavingExpiry(false);
    }
  };

  // Role Access Guard: Only ADMIN or SUPER_ADMIN
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 border border-red-200 rounded-2xl bg-red-50/50 p-6 text-center">
        <ShieldAlert size={36} className="text-red-500" />
        <div>
          <h3 className="text-base font-bold text-gray-800">Access Denied</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-md">
            Only Admin and Super Admin roles have permission to view or manage Membership Plan Pricing configurations.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <RefreshCw className="animate-spin text-[#2ab391]" size={30} />
        <span className="text-sm font-semibold text-gray-500">Loading Membership Configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div>
        <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <Settings size={18} className="text-[#2ab391]" />
          Memberships Management
        </h3>
        <p className="text-xs text-gray-400 mt-1 font-semibold">
          Configure annual membership pricing and Razorpay AutoPay Plan IDs for Company and Individual listing plans.
        </p>
      </div>

      {/* ── Section 1: Company & Individual Pricing Configuration ── */}
      <div className="space-y-4 max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Company Listing Plans */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs space-y-5 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="border-b border-gray-100 pb-2">
                <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <CreditCard size={16} className="text-[#2ab391]" />
                  Company Plans (AutoPay Mandate)
                </h4>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Uses recurring Razorpay Subscriptions with yearly AutoPay.
                </p>
              </div>

              {/* Professional Plan Price */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Company Professional Price (₹ / year) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    min={1}
                    value={form.professional_price}
                    onChange={(e) => setForm((f) => ({ ...f, professional_price: Number(e.target.value) }))}
                    placeholder="e.g. 12000"
                    className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
                  />
                </div>
              </div>

              {/* Professional AutoPay Plan ID */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Professional AutoPay Plan ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.professional_plan_id}
                  onChange={(e) => setForm((f) => ({ ...f, professional_plan_id: e.target.value }))}
                  placeholder="e.g. plan_TabHs7Uuj3A13W"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-mono focus:outline-none focus:border-[#2ab391] transition-all"
                />
              </div>

              <hr className="border-gray-100" />

              {/* Premium Plan Price */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Company Premium Price (₹ / year) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    min={1}
                    value={form.premium_price}
                    onChange={(e) => setForm((f) => ({ ...f, premium_price: Number(e.target.value) }))}
                    placeholder="e.g. 25000"
                    className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
                  />
                </div>
              </div>

              {/* Premium AutoPay Plan ID */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Premium AutoPay Plan ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.premium_plan_id}
                  onChange={(e) => setForm((f) => ({ ...f, premium_plan_id: e.target.value }))}
                  placeholder="e.g. plan_TabLDPpcFqUt7Z"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-mono focus:outline-none focus:border-[#2ab391] transition-all"
                />
              </div>
            </div>
          </div>

          {/* Individual Listing Plans */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs space-y-5 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="border-b border-gray-100 pb-2">
                <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <CreditCard size={16} className="text-[#2ab391]" />
                  Individual Plans (AutoPay Mandate)
                </h4>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Uses recurring Razorpay Subscriptions with yearly AutoPay.
                </p>
              </div>

              {/* Individual Professional Price */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Individual Professional Price (₹ / year) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    min={1}
                    value={form.individual_professional_price}
                    onChange={(e) => setForm((f) => ({ ...f, individual_professional_price: Number(e.target.value) }))}
                    placeholder="e.g. 4000"
                    className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
                  />
                </div>
              </div>

              {/* Individual Professional AutoPay Plan ID */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Individual Professional Plan ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.individual_professional_plan_id}
                  onChange={(e) => setForm((f) => ({ ...f, individual_professional_plan_id: e.target.value }))}
                  placeholder="e.g. plan_TiFXhEqd1Msk0m"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-mono focus:outline-none focus:border-[#2ab391] transition-all"
                />
              </div>

              <hr className="border-gray-100" />

              {/* Individual Premium Price */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Individual Premium Price (₹ / year) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    min={1}
                    value={form.individual_premium_price}
                    onChange={(e) => setForm((f) => ({ ...f, individual_premium_price: Number(e.target.value) }))}
                    placeholder="e.g. 7000"
                    className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
                  />
                </div>
              </div>

              {/* Individual Premium AutoPay Plan ID */}
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                  Individual Premium Plan ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.individual_premium_plan_id}
                  onChange={(e) => setForm((f) => ({ ...f, individual_premium_plan_id: e.target.value }))}
                  placeholder="e.g. plan_TiFaJ2pWDUDWay"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-mono focus:outline-none focus:border-[#2ab391] transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dedicated Save Button for Company & Individual Plans */}
        <div>
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={savingPlans}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-colors active:scale-[0.98] shadow-md shadow-[#2ab391]/10 cursor-pointer ${
              savedPlansOk
                ? 'bg-emerald-500 text-white'
                : 'bg-[#2ab391] hover:bg-[#23977a] text-white disabled:opacity-60'
            }`}
          >
            {savingPlans ? <Loader size={16} className="animate-spin" /> : savedPlansOk ? <Check size={16} /> : <Save size={16} />}
            {savingPlans ? 'Saving Plans...' : savedPlansOk ? 'Saved Plans Successfully!' : 'Save Plans & Pricing'}
          </button>
        </div>
      </div>

      {/* ── Section 2: Free Plan Promotional Expiry (Mock Urgency) ── */}
      <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs space-y-5 max-w-4xl">
        <div className="border-b border-gray-100 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Clock size={16} className="text-[#2ab391]" />
              Free Plan Promotional Expiry (Mock Urgency)
            </h4>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Controls the live dynamic countdown timer displayed on the Free Plan card in the Vendor Upgrade Membership page.
            </p>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full self-start sm:self-auto">
            <Sparkles size={10} /> Urgency Display Only
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* Input & Presets */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
                Set Expiry Date & Time (IST)
              </label>
              <input
                type="datetime-local"
                value={form.free_plan_mock_expiry}
                onChange={(e) => setForm((f) => ({ ...f, free_plan_mock_expiry: e.target.value }))}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
              />
            </div>

            {/* Quick Presets */}
            <div>
              <span className="block text-[11px] text-gray-400 font-semibold mb-1.5">Quick Presets:</span>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => applyPreset('24h')}
                  className="px-2.5 py-1 text-xs font-semibold bg-gray-50 hover:bg-[#eaf7f4] hover:text-[#2ab391] hover:border-[#2ab391]/40 border border-gray-200 rounded-lg transition-all cursor-pointer"
                >
                  +24 Hours
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('3d')}
                  className="px-2.5 py-1 text-xs font-semibold bg-gray-50 hover:bg-[#eaf7f4] hover:text-[#2ab391] hover:border-[#2ab391]/40 border border-gray-200 rounded-lg transition-all cursor-pointer"
                >
                  +3 Days
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('7d')}
                  className="px-2.5 py-1 text-xs font-semibold bg-gray-50 hover:bg-[#eaf7f4] hover:text-[#2ab391] hover:border-[#2ab391]/40 border border-gray-200 rounded-lg transition-all cursor-pointer"
                >
                  +7 Days
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('eom')}
                  className="px-2.5 py-1 text-xs font-semibold bg-gray-50 hover:bg-[#eaf7f4] hover:text-[#2ab391] hover:border-[#2ab391]/40 border border-gray-200 rounded-lg transition-all cursor-pointer"
                >
                  End of Month
                </button>
                {form.free_plan_mock_expiry && (
                  <button
                    type="button"
                    onClick={() => applyPreset('clear')}
                    className="px-2.5 py-1 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-all cursor-pointer"
                  >
                    Clear / Reset
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Live Preview Box */}
          <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-4 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase text-gray-500 tracking-wider">
                Vendor Card Live Preview
              </span>
              <span className="text-[10px] font-bold text-[#2ab391] bg-[#eaf7f4] px-2 py-0.5 rounded">
                Live
              </span>
            </div>

            {/* Render mock timer badge matching vendor side */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-2xs space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 font-semibold text-[11px] text-gray-600">
                  <Clock size={12} className="text-gray-500 animate-pulse flex-shrink-0" />
                  Offer Ends In:
                </span>
                <span className="font-mono font-bold text-gray-800 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-[11px] tracking-wider">
                  {previewInfo && !previewInfo.isExpired
                    ? previewInfo.countdown
                    : '23h 59m 59s (Rolling 24h fallback)'}
                </span>
              </div>
            </div>

            <p className="text-[11px] text-gray-500 font-medium">
              {form.free_plan_mock_expiry ? (
                previewInfo?.isExpired ? (
                  <span className="text-amber-600 font-semibold">⚠ Configured date is in the past; vendor UI will use automatic 24-hour rolling timer until updated.</span>
                ) : (
                  <span>Target Expiry: <strong className="text-gray-800">{previewInfo?.dateFormatted}</strong></span>
                )
              ) : (
                <span>No fixed date set. Vendors will see the default rolling 24-hour countdown timer.</span>
              )}
            </p>
          </div>
        </div>

        {/* Dedicated Save Button for Free Plan Mock Expiry */}
        <div className="pt-2 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={handleSaveExpiry}
            disabled={savingExpiry}
            className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs transition-colors active:scale-[0.98] shadow-xs cursor-pointer ${
              savedExpiryOk
                ? 'bg-emerald-500 text-white'
                : 'bg-[#2ab391] hover:bg-[#23977a] text-white disabled:opacity-60'
            }`}
          >
            {savingExpiry ? <Loader size={14} className="animate-spin" /> : savedExpiryOk ? <Check size={14} /> : <Save size={14} />}
            {savingExpiry ? 'Updating Expiry...' : savedExpiryOk ? 'Updated Expiry Successfully!' : 'Save Free Plan Expiry'}
          </button>
        </div>
      </div>

      {/* ── Confirmation Modal for Pricing & Plan IDs ── */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 text-left space-y-4 max-h-[90vh] overflow-y-auto transform transition-all">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                <AlertCircle className="text-amber-600" size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Confirm Membership Pricing & Plan IDs</h3>
                <p className="text-xs text-gray-500 font-medium">Please review Company & Individual plan pricing and AutoPay IDs before saving.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3.5 space-y-2 text-xs border border-gray-200">
              <div className="text-[11px] font-bold uppercase text-[#2ab391] tracking-wider mb-1">Company Listing Plans</div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1">
                <span className="text-gray-500 font-semibold">Professional Price:</span>
                <span className="font-bold text-gray-900">₹{Number(form.professional_price).toLocaleString('en-IN')}/year</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1">
                <span className="text-gray-500 font-semibold">Professional AutoPay ID:</span>
                <span className="font-mono font-bold text-gray-800">{form.professional_plan_id}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1">
                <span className="text-gray-500 font-semibold">Premium Price:</span>
                <span className="font-bold text-gray-900">₹{Number(form.premium_price).toLocaleString('en-IN')}/year</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-2">
                <span className="text-gray-500 font-semibold">Premium AutoPay ID:</span>
                <span className="font-mono font-bold text-gray-800">{form.premium_plan_id}</span>
              </div>

              <div className="text-[11px] font-bold uppercase text-[#2ab391] tracking-wider pt-1 mb-1">Individual Listing Plans</div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1">
                <span className="text-gray-500 font-semibold">Individual Professional:</span>
                <span className="font-bold text-gray-900">₹{Number(form.individual_professional_price).toLocaleString('en-IN')}/year</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1">
                <span className="text-gray-500 font-semibold">Individual Pro AutoPay ID:</span>
                <span className="font-mono font-bold text-gray-800">{form.individual_professional_plan_id}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1">
                <span className="text-gray-500 font-semibold">Individual Premium:</span>
                <span className="font-bold text-gray-900">₹{Number(form.individual_premium_price).toLocaleString('en-IN')}/year</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 font-semibold">Individual Prem AutoPay ID:</span>
                <span className="font-mono font-bold text-gray-800">{form.individual_premium_plan_id}</span>
              </div>
            </div>

            <p className="text-xs text-gray-600 font-medium leading-relaxed">
              Are you sure you want to save these updated membership configurations? All future vendor plan purchases and AutoPay subscriptions will immediately use these new rates.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={savingPlans}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  handleSavePlans();
                }}
                disabled={savingPlans}
                className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white text-xs font-bold rounded-xl transition-colors shadow-xs cursor-pointer"
              >
                Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipsManagementTab;
